class ShippingNotice extends HTMLElement {
  constructor() {
    super();
    this.messageEl = this.querySelector('.shipping-notice-message');
    this.barEl = this.querySelector('.shipping-progress-bar');
    this.fillEl = this.querySelector('.shipping-progress-bar__fill');
    this.truckEl = this.querySelector('.shipping-progress-truck');
  }

  connectedCallback() {
    this.updateState();
    this.updatePromoMessage();

    // Watch for attribute changes
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.type === 'attributes' &&
          (mutation.attributeName === 'data-cart-total-amount' ||
            mutation.attributeName === 'data-free-shipping')
        ) {
          this.updateState();
          this.updatePromoMessage();
        }
      });
    });
    observer.observe(this, { attributes: true });

    // Also listen to cart update events for gift promo message
    document.addEventListener('cart:updated', () => {
      this.updatePromoMessage();
    });
  }

  updatePromoMessage() {
    // Get cart total from shipping notice or fetch from cart
    let cartTotal = Number(this.getAttribute('data-cart-total-amount')) || 0;
    const currencyRate = window.Shopify?.currency?.rate ? Number(window.Shopify.currency.rate) : 1;

    // If no cart total from shipping notice, fetch it
    if (cartTotal === 0) {
      fetch('/cart.js')
        .then((response) => response.json())
        .then((cart) => {
          this.updatePromoMessagesWithTotal(cart.total_price, currencyRate);
        })
        .catch(() => {
          // If fetch fails, try with 0
          this.updatePromoMessagesWithTotal(0, currencyRate);
        });
      return;
    }

    this.updatePromoMessagesWithTotal(cartTotal, currencyRate);
  }

  updatePromoMessagesWithTotal(cartTotal, currencyRate) {
    // Update free shipping promo message
    const shippingPromoMessage = document.querySelector('[data-promo-message-shipping]');
    if (shippingPromoMessage) {
      const freeShippingThreshold = Number(this.getAttribute('data-free-shipping')) || 0;
      const adjustedShippingThreshold = Math.round(freeShippingThreshold * currencyRate);
      const remainingShippingAmount = adjustedShippingThreshold - cartTotal;

      // Hide shipping promo message when goal is reached
      if (remainingShippingAmount <= 0) {
        shippingPromoMessage.style.display = 'none';
      } else {
        shippingPromoMessage.style.display = 'block';
      }
    }

    // Update free gift promo message
    const giftPromoMessage = document.querySelector('[data-promo-message-gift]');
    if (giftPromoMessage) {
      const freeGiftAmount = giftPromoMessage.dataset.freeGiftAmount 
        ? Number(giftPromoMessage.dataset.freeGiftAmount) 
        : 0;
      
      if (freeGiftAmount > 0) {
        const adjustedGiftThreshold = Math.round(freeGiftAmount * currencyRate);
        const remainingGiftAmount = adjustedGiftThreshold - cartTotal;

        // Hide gift promo message when goal is reached
        if (remainingGiftAmount <= 0) {
          giftPromoMessage.style.display = 'none';
        } else {
          giftPromoMessage.style.display = 'block';
        }
      }
    }
  }

  updateState() {
    const cartTotal = Number(this.getAttribute('data-cart-total-amount')) || 0;

    if (cartTotal === 0) {
      this.classList.add('is-empty');
      return;
    } else {
      this.classList.remove('is-empty');
    }

    const freeShippingThreshold = Number(this.getAttribute('data-free-shipping')) || 0;
    const currencyRate = window.Shopify?.currency?.rate ? Number(window.Shopify.currency.rate) : 1;
    const adjustedThreshold = Math.round(freeShippingThreshold * currencyRate);

    const remainingAmount = adjustedThreshold - cartTotal;
    let progressPercent = 0;
    let shippingMessage = '';
    let shippingSuccess = false;

    if (remainingAmount > 0) {
      const formattedAmount = this.formatMoney(remainingAmount);
      shippingMessage = window.cartStrings?.free_shipping_remaining_html?.replace(
        '{{ remaining_amount }}',
        formattedAmount
      ) || `You're ${formattedAmount} away from free shipping.`;
      shippingSuccess = false;
      progressPercent = Math.max(0, Math.min(100, (cartTotal / adjustedThreshold) * 100));
    } else {
      shippingMessage =
        window.cartStrings?.free_shipping_eligible || "You are eligible for free shipping!";
      shippingSuccess = true;
      progressPercent = 100;
    }

    this.classList.toggle('is-success', shippingSuccess);
    
    // Check if we should hide the shipping notice when goal is reached
    const hideOnSuccess = this.dataset.hideOnSuccess === 'true';
    if (hideOnSuccess && shippingSuccess) {
      this.style.display = 'none';
    } else {
      this.style.display = 'block';
    }
    
    if (this.messageEl) {
      this.messageEl.textContent = shippingMessage;
    }
    if (this.barEl) {
      this.barEl.setAttribute('aria-valuenow', cartTotal);
      this.barEl.setAttribute('aria-valuemax', adjustedThreshold);
    }
    if (this.fillEl) {
      this.fillEl.style.width = `${progressPercent}%`;
    }
    if (this.truckEl) {
      this.truckEl.style.left = `${progressPercent}%`;
    }

    // Hide promo message when goal is reached
    this.updatePromoMessage();
  }

  formatMoney(cents) {
    if (typeof cents === 'string') {
      cents = cents.replace('.', '');
    }

    let value = '';
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const formatString = window.Shopify?.format?.money || '{{amount}}';

    function formatWithDelimiters(number, precision, thousands, decimal) {
      thousands = thousands || ',';
      decimal = decimal || '.';

      if (isNaN(number) || number === null) {
        return 0;
      }

      number = (number / 100.0).toFixed(precision);

      const parts = number.split('.');
      const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const centsAmount = parts[1] ? decimal + parts[1] : '';

      return dollarsAmount + centsAmount;
    }

    const match = formatString.match(placeholderRegex);
    if (match) {
      switch (match[1]) {
        case 'amount':
          value = formatWithDelimiters(cents, 2);
          break;
        case 'amount_no_decimals':
          value = formatWithDelimiters(cents, 0);
          break;
        case 'amount_with_comma_separator':
          value = formatWithDelimiters(cents, 2, '.', ',');
          break;
        case 'amount_no_decimals_with_comma_separator':
          value = formatWithDelimiters(cents, 0, '.', ',');
          break;
        case 'amount_no_decimals_with_space_separator':
          value = formatWithDelimiters(cents, 0, ' ');
          break;
        case 'amount_with_apostrophe_separator':
          value = formatWithDelimiters(cents, 2, "'");
          break;
        default:
          value = formatWithDelimiters(cents, 2);
      }
    }

    return formatString.replace(placeholderRegex, value);
  }
}

if (!customElements.get('shipping-notice')) {
  customElements.define('shipping-notice', ShippingNotice);
}

